
import { propertyTypes, featuresList, locations } from '../../../mocks/properties';

interface Filters {
  type: string;
  minPrice: number;
  maxPrice: number;
  bedrooms: number;
  location: string;
  features: string[];
  minArea: number;
  maxArea: number;
}

interface FiltersPanelProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  onClearFilters: () => void;
  isMobile?: boolean;
  onClose?: () => void;
}

const FiltersPanel = ({ filters, setFilters, onClearFilters, isMobile, onClose }: FiltersPanelProps) => {
  const bedroomOptions = [1, 2, 3, 4, 5];

  const handleTypeChange = (type: string) => {
    setFilters({ ...filters, type: filters.type === type ? '' : type });
  };

  const handleBedroomChange = (bedrooms: number) => {
    setFilters({ ...filters, bedrooms: filters.bedrooms === bedrooms ? 0 : bedrooms });
  };

  const handleFeatureChange = (feature: string) => {
    const newFeatures = filters.features.includes(feature)
      ? filters.features.filter(f => f !== feature)
      : [...filters.features, feature];
    setFilters({ ...filters, features: newFeatures });
  };

  const formatPrice = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    }
    return `R$ ${(value / 1000).toFixed(0)}K`;
  };

  return (
    <div className={`bg-white rounded-2xl shadow-lg p-6 ${isMobile ? 'h-full overflow-y-auto' : 'sticky top-24'}`}>
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Filtros</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onClearFilters}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            Limpar
          </button>
          {isMobile && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          )}
        </div>
      </div>

      {/* Tipo de Imóvel */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Tipo de Imóvel
        </label>
        <div className="grid grid-cols-2 gap-2">
          {propertyTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => handleTypeChange(type.value)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                filters.type === type.value
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`${type.icon} text-base`}></i>
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Faixa de Preço */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Faixa de Preço
        </label>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{formatPrice(filters.minPrice)}</span>
            <span>{formatPrice(filters.maxPrice)}</span>
          </div>
          <input
            type="range"
            min="100000"
            max="5000000"
            step="50000"
            value={filters.minPrice}
            onChange={(e) => setFilters({ ...filters, minPrice: Number(e.target.value) })}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          <input
            type="range"
            min="100000"
            max="5000000"
            step="50000"
            value={filters.maxPrice}
            onChange={(e) => setFilters({ ...filters, maxPrice: Number(e.target.value) })}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
        </div>
      </div>

      {/* Quartos */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Quartos
        </label>
        <div className="flex gap-2">
          {bedroomOptions.map((num) => (
            <button
              key={num}
              onClick={() => handleBedroomChange(num)}
              className={`w-10 h-10 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                filters.bedrooms === num
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {num === 5 ? '5+' : num}
            </button>
          ))}
        </div>
      </div>

      {/* Localização */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Localização
        </label>
        <select
          value={filters.location}
          onChange={(e) => setFilters({ ...filters, location: e.target.value })}
          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
        >
          <option value="">Todas as regiões</option>
          {locations.map((loc) => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
        </select>
      </div>

      {/* Área */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Área (m²)
        </label>
        <div className="flex gap-3">
          <input
            type="number"
            placeholder="Mín"
            value={filters.minArea || ''}
            onChange={(e) => setFilters({ ...filters, minArea: Number(e.target.value) })}
            className="w-1/2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          <input
            type="number"
            placeholder="Máx"
            value={filters.maxArea || ''}
            onChange={(e) => setFilters({ ...filters, maxArea: Number(e.target.value) })}
            className="w-1/2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Características */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Características
        </label>
        <div className="space-y-2">
          {featuresList.map((feature) => (
            <label
              key={feature.value}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                  filters.features.includes(feature.value)
                    ? 'bg-teal-600 border-teal-600'
                    : 'border-gray-300 group-hover:border-teal-400'
                }`}
              >
                {filters.features.includes(feature.value) && (
                  <i className="ri-check-line text-white text-sm"></i>
                )}
              </div>
              <span className="text-sm text-gray-700">{feature.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Botão Aplicar (Mobile) */}
      {isMobile && (
        <button
          onClick={onClose}
          className="w-full py-3.5 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Aplicar Filtros
        </button>
      )}
    </div>
  );
};

export default FiltersPanel;
